import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import {
  AlertTriangle, CheckCircle, Clock, Search, Bell,
  XCircle, Eye
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import ListPageHeader from '../../components/shared/ListPageHeader';
import { intelligenceAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const AlertsCenterPage = () => {
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [filterStatus, setFilterStatus] = useState('OPEN');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (activeBusiness?.id) {
      fetchAlerts();
    }
  }, [activeBusiness?.id, filterStatus, filterSeverity]);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus && filterStatus !== 'ALL') params.status = filterStatus;
      if (filterSeverity) params.severity = filterSeverity;

      const { data } = await intelligenceAPI.getAlerts(params);
      setAlerts(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load alerts'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (alertId, newStatus) => {
    try {
      await intelligenceAPI.updateAlertStatus(alertId, { status: newStatus });
      toast.success(`Alert ${newStatus.toLowerCase()}`);
      fetchAlerts();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update alert'));
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'bg-rose-50 text-rose-700';
      case 'WARN': return 'bg-amber-50 text-amber-700';
      default: return 'bg-blue-50 text-blue-700';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '---';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openCount = alerts.filter(a => a.status === 'OPEN').length;
  const criticalCount = alerts.filter(a => a.severity === 'CRITICAL').length;

  const filteredAlerts = alerts.filter(alert =>
    !searchQuery ||
    alert.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    alert.entity_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    alert.message?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col" data-testid="alerts-center-page">
      {/* Premium Header */}
      <ListPageHeader
        title="Alerts Center"
        subtitle="Monitor and manage business alerts and notifications"
        stats={[
          { icon: Bell, label: 'Total Alerts', value: alerts.length, color: 'slate' },
          { icon: Clock, label: 'Open', value: openCount, color: 'amber' },
          { icon: AlertTriangle, label: 'Critical', value: criticalCount, color: 'rose' },
        ]}
        secondaryAction={{
          label: 'Overview',
          icon: Eye,
          onClick: () => navigate(`/business/${activeBusiness?.id}/intelligence/overview`)
        }}
      />

      {/* Controls Bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search alerts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[13px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>
            <div className="flex items-center gap-1 bg-slate-50 rounded-lg p-1 border border-slate-200">
              {['ALL', 'OPEN', 'ACKNOWLEDGED', 'RESOLVED'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-3 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                    filterStatus === status
                      ? 'bg-white text-teal-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Table Area */}
      <div className="flex-1 overflow-hidden flex flex-col px-2 pb-2 pt-2">
        <div className="flex-1 flex flex-col bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-[13px] text-slate-500 font-normal">Loading alerts...</p>
              </div>
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
                <Bell className="w-7 h-7 text-slate-400" />
              </div>
              <p className="text-[15px] font-medium text-slate-600">No alerts found</p>
              <p className="text-[13px] text-slate-400 mt-1">All clear! No alerts match your filters</p>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <table className="tl-table table-fixed w-full">
                  <colgroup>
                    <col style={{ width: '28%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '18%' }} />
                    <col style={{ width: '16%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '14%' }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="tl-th tl-th-teal sticky top-0 z-10 bg-white">Alert</th>
                      <th className="tl-th tl-th-teal sticky top-0 z-10 bg-white">Severity</th>
                      <th className="tl-th tl-th-teal sticky top-0 z-10 bg-white">Related To</th>
                      <th className="tl-th tl-th-teal sticky top-0 z-10 bg-white">Created</th>
                      <th className="tl-th tl-th-teal sticky top-0 z-10 bg-white">Status</th>
                      <th className="tl-th tl-th-teal sticky top-0 z-10 bg-white text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="animate-stagger">
                    {filteredAlerts.map((alert, idx) => (
                      <tr
                        key={alert.id}
                        className={`tl-tr hover:bg-teal-50/40 animate-tableRowFadeIn ${idx % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
                        style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                      >
                        <td className="tl-td">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getSeverityColor(alert.severity)}`}>
                              <AlertTriangle className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="text-[13px] font-semibold text-slate-800">{alert.title || alert.alert_type?.replace(/_/g, ' ')}</p>
                              <p className="text-[11px] text-slate-500 max-w-[200px] truncate">{alert.message}</p>
                            </div>
                          </div>
                        </td>
                        <td className="tl-td">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium ${getSeverityColor(alert.severity)}`}>
                            {alert.severity}
                          </span>
                        </td>
                        <td className="tl-td">
                          <p className="text-[13px] text-slate-700">{alert.entity_name || '---'}</p>
                          <p className="text-[11px] text-slate-500 capitalize">{alert.entity_type || ''}</p>
                        </td>
                        <td className="tl-td">
                          <p className="text-[12px] text-slate-600">{formatDate(alert.created_at)}</p>
                        </td>
                        <td className="tl-td">
                          {alert.status === 'OPEN' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full text-[10px] font-medium">
                              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                              Open
                            </span>
                          ) : alert.status === 'ACKNOWLEDGED' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-[10px] font-medium">
                              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                              Acknowledged
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-medium">
                              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                              Resolved
                            </span>
                          )}
                        </td>
                        <td className="tl-td text-right">
                          <div className="flex items-center justify-end gap-1">
                            {alert.status === 'OPEN' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleUpdateStatus(alert.id, 'ACKNOWLEDGED'); }}
                                className="p-1.5 hover:bg-slate-100 rounded-md transition-colors"
                                title="Acknowledge"
                              >
                                <CheckCircle className="w-4 h-4 text-blue-500" />
                              </button>
                            )}
                            {alert.status !== 'RESOLVED' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleUpdateStatus(alert.id, 'RESOLVED'); }}
                                className="p-1.5 hover:bg-slate-100 rounded-md transition-colors"
                                title="Resolve"
                              >
                                <XCircle className="w-4 h-4 text-emerald-500" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-4 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[12px] text-slate-500">
                <p>
                  Showing <span className="font-medium text-slate-700">{filteredAlerts.length}</span> of <span className="font-medium text-slate-700">{alerts.length}</span> alerts
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertsCenterPage;
